import React, { useState, useRef } from 'react'
import { Box, TextField, useTheme } from '@mui/material'

const CodeInput: React.FC<{
    length?: number
    disabled?: boolean
    onComplete?: (code: string) => void
}> = ({ length = 4, onComplete, disabled }) => {
    const [values, setValues] = useState<string[]>(Array(length).fill(''))
    const inputRefs = useRef<(HTMLInputElement | null)[]>([])

    const handleChange = (index: number, value: string) => {
        if (/^\d$/.test(value) || value === '') {
            const newValues = [...values]
            newValues[index] = value
            setValues(newValues)
            // Automatically move to the next input
            if (value !== '' && index < length - 1) {
                inputRefs.current[index + 1]?.focus()
            }
            // Trigger onComplete when all inputs are filled
            if (
                newValues.join('').length === length &&
                !newValues.includes('')
            ) {
                onComplete?.(newValues.join(''))
            }
        }
    }

    const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
        if (event.key === 'Backspace') {
            if (index > 0) {
                inputRefs.current[index - 1]?.focus()
            }
            handleChange(index, '')
        }
        if (event.key === 'Delete') {
            if (index + 1 < length) {
                const newValues = [...values]
                newValues.splice(index, 1)
                setValues([...newValues, ''])
            }
        } else {
            handleChange(index, event.key)
        }
    }

    const handlePaste = (event: React.ClipboardEvent) => {
        event.preventDefault()
        const pastedData = event.clipboardData
            .getData('text')
            .replace(/\D/g, '') // Only keep numeric characters
        if (pastedData) {
            const newValues = [...values]
            for (let i = 0; i < length; i++) {
                if (i < pastedData.length) {
                    newValues[i] = pastedData[i]
                } else {
                    newValues[i] = ''
                }
            }
            setValues(newValues)

            // Trigger onComplete if all inputs are filled
            if (
                newValues.join('').length === length &&
                !newValues.includes('')
            ) {
                onComplete?.(newValues.join(''))
            }

            // Focus the appropriate input field
            const nextIndex = Math.min(pastedData.length, length - 1)
            inputRefs.current[nextIndex]?.focus()
        }
    }

    const theme = useTheme()
    return (
        <Box
            display="flex"
            alignContent={'center'}
            justifyContent={'space-around'}
            gap={1}
        >
            {values.map((value, index) => (
                <TextField
                    disabled={disabled}
                    key={index}
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    value={value}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    sx={{
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        width: '3rem',

                        [theme.breakpoints.down('sm')]: {
                            width: '2rem',
                        },
                    }}
                    inputProps={{
                        maxLength: 1,
                    }}
                />
            ))}
        </Box>
    )
}

export default CodeInput
